import React from 'react';
import MetricCard from './MetricCard';
import { AlertTriangle, Tag, MessageSquare, Clock } from 'lucide-react';

interface MetricsGridProps {
  timeRange?: string;
}

const MetricsGrid: React.FC<MetricsGridProps> = ({ timeRange = 'Last 1h' }) => {
  // You can use timeRange to filter metrics data based on the selected time range
  console.log('MetricsGrid received time range:', timeRange);
  
  // Define dynamic metric values based on time range
  interface MetricValues {
    [key: string]: {
      schemaErrors: string;
      schemaErrorsChange: string;
      lagInMessages: string;
      lagInTime: string;
      lagInMessagesChange: string;
      lagInTimeChange: string;
    };
  }
  
  const metricValues: MetricValues = {
    'Last 10m': {
      schemaErrors: 'No data',
      schemaErrorsChange: '--',
      lagInMessages: '5342',
      lagInTime: '2m 18s',
      lagInMessagesChange: '+3.2%',
      lagInTimeChange: '+0.5%'
    },
    'Last 30m': {
      schemaErrors: 'No data',
      schemaErrorsChange: '--',
      lagInMessages: '8791',
      lagInTime: '3m 42s',
      lagInMessagesChange: '+5.8%',
      lagInTimeChange: '+1.2%'
    },
    'Last 1h': {
      schemaErrors: '12',
      schemaErrorsChange: '+4',
      lagInMessages: '23101',
      lagInTime: '8m 17s',
      lagInMessagesChange: '+12.4%',
      lagInTimeChange: '+3.7%'
    },
    'Last 6h': {
      schemaErrors: '37',
      schemaErrorsChange: '+15',
      lagInMessages: '98765',
      lagInTime: '21m 34s',
      lagInMessagesChange: '-2.1%',
      lagInTimeChange: '-0.9%'
    },
    'Last 24h': {
      schemaErrors: '142',
      schemaErrorsChange: '+45',
      lagInMessages: '187432',
      lagInTime: '43m 12s',
      lagInMessagesChange: '-5.3%',
      lagInTimeChange: '-2.7%'
    }
  };
  
  // Get values for the current time range or fallback to default
  const currentMetrics = metricValues[timeRange] || metricValues['Last 1h'];
  
  // Function to determine trend based on change value
  const getTrend = (changeValue: string): 'up' | 'down' => {
    if (changeValue.startsWith('+')) return 'up';
    if (changeValue.startsWith('-')) return 'down';
    return 'up'; // Default to up if format is unexpected
  };
  
  const metrics = [
    {
      title: 'Schema Errors',
      value: currentMetrics.schemaErrors,
      change: currentMetrics.schemaErrorsChange,
      trend: currentMetrics.schemaErrors === 'No data' ? 'down' as const : 'up' as const,
      icon: AlertTriangle,
      color: currentMetrics.schemaErrors === 'No data' ? '#10b981' : '#ef4444'
    },
    {
      title: 'Version',
      value: '2.28.1',
      change: '--',
      trend: 'up' as const,
      icon: Tag,
      color: '#6366f1'
    },
    {
      title: 'Lag in messages',
      value: currentMetrics.lagInMessages,
      change: currentMetrics.lagInMessagesChange,
      trend: getTrend(currentMetrics.lagInMessagesChange),
      icon: MessageSquare,
      color: '#10b981'
    },
    {
      title: 'Lag in time',
      value: currentMetrics.lagInTime,
      change: currentMetrics.lagInTimeChange,
      trend: getTrend(currentMetrics.lagInTimeChange),
      icon: Clock,
      color: '#6366f1'
    },
  ];

  return (
    <div className="metrics-grid">
      {metrics.map((metric, index) => (
        <MetricCard key={index} {...metric} />
      ))}
    </div>
  );
};

export default MetricsGrid;
